/**
 * Kehityssuunnitelma, tavoitteet ja one-to-one-keskustelut
 * (docs/KEHITYSSUUNNITELMAT.md, luvut 7–8).
 *
 * Tämä on moduulin **prosessin** puoli. Lopputuloksen puoli —
 * osaamisrekisteri — on `osaaminen.ts`:ssä, ja ne on pidetty erillään
 * siksi, että niillä on eri lukijat ja eri elinkaari (luku 4): rekisteri
 * on tarjousliitteen aineistoa ja elää pitkään, prosessi on esihenkilön
 * ja työntekijän välistä ja katoaa säilytysajan päätyttyä.
 *
 * Täällä on tilalogiikka **puhtaina funktioina**: mitkä tavoitteet ovat
 * avoimia, mikä on myöhässä, ja saako keskustelun sulkea. Samat selaimessa
 * ja palvelimella — sulkemisnappi on harmaana samasta syystä, jolla
 * palvelin torjuu sulkemisen, eikä kahta toteutusta samalle säännölle voi
 * pitää synkassa.
 */
import { daysUntil } from './trainings.js';
// --- Tavoitteen tila -------------------------------------------------------
/**
 * Tavoitteen tila.
 *
 * Neljä, ei viittä. "Lykätty" ei ole tila vaan tavoitepäivän siirto, ja
 * siirto näkyy siitä, että edellisen keskustelun tilannekuvassa oli eri
 * `targetOn`. Viides tila pakottaisi jokaisen näkymän päättämään
 * erikseen, lasketaanko lykätty avoimeksi — ja vastaus olisi joka kerta
 * kyllä.
 */
export const KEHITYSTAVOITE_TILAT = ['PLANNED', 'IN_PROGRESS', 'ACHIEVED', 'DROPPED'];
/**
 * Onko tavoite yhä auki, eli onko siitä keskusteltava.
 *
 * Tämä on se raja, joka ratkaisee sulkemisen (`puuttuvatTilannekuvat`) ja
 * hallinnan yleiskatsauksen laskurit. Yksi määritelmä, koska kaksi
 * eriytyisi: keskustelun voisi sulkea ilman tilannekuvaa tavoitteesta,
 * jonka yleiskatsaus laskee avoimeksi.
 */
export function onAvoinTavoite(tila) {
    return tila === 'PLANNED' || tila === 'IN_PROGRESS';
}
/**
 * Onko avoin tavoite myöhässä tavoitepäivästään.
 *
 * Suljettu tavoite ei ole myöhässä, vaikka se olisi saavutettu
 * myöhään — myöhästyminen on toimenpide, ja saavutetulle ei ole
 * toimenpidettä. Tavoitepäivä itse ei vielä ole myöhässä: se on
 * viimeinen päivä, ei ensimmäinen myöhäinen, sama tulkinta kuin
 * sertifikaatin vanhenemispäivällä.
 */
export function onMyohassa(tavoite, tanaan) {
    return onAvoinTavoite(tavoite.status) && daysUntil(tanaan, tavoite.targetOn) < 0;
}
// --- Kehityspolut ----------------------------------------------------------
/**
 * Polun askeleen laji (docs/KEHITYSSUUNNITELMAT.md, luku 16).
 *
 * `RESOURCE` on kolmas siksi, että polun hyödyllisin askel ei aina ole
 * tentti: opiskelumateriaali, jota ei voi suorittaa, on silti osa sitä,
 * mihin sitoudutaan. Ilman tätä lajia se joko jätettäisiin pois tai
 * väännettäisiin sertifikaatiksi, jota kukaan ei voi saavuttaa.
 */
export const KEHITYSPOLKU_ASKELLAJIT = ['CERTIFICATION', 'TRAINING', 'RESOURCE'];
/**
 * Kuinka olennainen askel on polulle.
 *
 * Kolme tasoa eikä pelkkä "pakollinen vai ei": polun jatko-osa (AZ-305,
 * DOP-C02) on eri asia kuin suositeltu aloitus (AZ-900), ja niiden
 * niputtaminen tekisi polusta joko liian raskaan tai liian ympäripyöreän
 * juuri siinä kohdassa, jota varten sivulle tullaan.
 */
export const KEHITYSPOLKU_ROOLIT = ['REQUIRED', 'RECOMMENDED', 'OPTIONAL'];
/**
 * Kutsujan tilanne yhdessä askeleessa.
 *
 * **Laskettu, ei tallennettu** (luku 16): askel on suoritettu, jos sen
 * sertifikaatti on voimassa rekisterissä tai koulutus suoritettuna — ei
 * jos joku merkitsi ruudun. `RESOURCE`-askel on aina `EI_ALOITETTU`:
 * materiaalin lukemisesta ei jää järjestelmään jälkeä, eikä sellaista
 * kannata teeskennellä.
 */
export const KEHITYSPOLKU_ASKELTILAT = ['SUORITETTU', 'TAVOITTEENA', 'EI_ALOITETTU'];
/** Polkukoodin ja askelavaimen muoto. Sama sääntö luettelossa ja kannassa. */
export const KEHITYSPOLKU_KOODI_PATTERN = /^[A-Z][A-Z0-9_]{1,39}$/;
export const KEHITYSPOLKU_ASKEL_PATTERN = /^[a-z0-9][a-z0-9-]{1,59}$/;
// --- Keskustelun sulkeminen ------------------------------------------------
/** Kuinka monen kuukauden välein keskustelu käydään, kun muuta ei ole sovittu. */
export const KESKUSTELUVALI_KUUKAUTTA = 6;
/**
 * Mitkä avoimet tavoitteet ovat yhä ilman tilannekuvaa tässä
 * keskustelussa.
 *
 * Sulkeminen vaatii tilannekuvan **jokaiselta avoimelta tavoitteelta**
 * (luku 8): kokous ei saa ohittaa tavoitetta hiljaa, ja jos siitä ei ole
 * mitään sanottavaa, sekin sanotaan. Saavutetuille ja hylätyille pääte
 * kirjoittaa tilannekuvan itse sulkemisen yhteydessä, joten ne eivät ole
 * täällä.
 *
 * Palauttaa tunnisteet eikä lukumäärää: käyttöliittymä korostaa juuri ne
 * rivit, joilta puuttuu, ja pelkkä luku pakottaisi etsimään ne käsin.
 */
export function puuttuvatTilannekuvat(tavoitteet, tilannekuvaGoalIds) {
    const kirjatut = new Set(tilannekuvaGoalIds);
    return tavoitteet
        .filter((tavoite) => onAvoinTavoite(tavoite.status) && !kirjatut.has(tavoite.id))
        .map((tavoite) => tavoite.id);
}
// --- Seuraava keskustelu ---------------------------------------------------
/**
 * Milloin seuraava keskustelu erääntyy.
 *
 * Järjestys on tarkoituksellinen ja sama kuin muistutuksella (luku 9):
 *
 *   1. **Sovittu päivä voittaa.** Se on ihmisten päätös, ja oletus on
 *      vain arvaus siitä, mitä he olisivat päättäneet.
 *   2. Muuten viimeisin suljettu keskustelu + oletusväli.
 *   3. Jos suljettua keskustelua ei ole, `null` — "erääntynyt heti" olisi
 *      väärä vastaus uudelle työntekijälle, ja sen päättää muistutus
 *      tilin iän perusteella, ei tämä funktio.
 */
export function seuraavaKeskusteluErääntyy(viimeisin) {
    if (viimeisin === null)
        return null;
    if (viimeisin.nextReviewOn !== null)
        return viimeisin.nextReviewOn;
    return lisaaKuukausia(viimeisin.heldOn, KESKUSTELUVALI_KUUKAUTTA);
}
/**
 * Kuukausien lisäys kalenteripäivään, kuun viimeinen päivä typistäen.
 *
 * Sama laskutapa kuin koulutuksen voimassaololla ja sertifikaatin
 * vanhenemisella (`oletusVanhenemisPaiva`). Kolmas toteutus samalle
 * asialle olisi kolmas paikka, jossa 31.8. + 6 kk voi olla 3.3.
 */
export function lisaaKuukausia(paiva, kuukausia) {
    const [vuosi, kuukausi, kpv] = paiva.split('-').map(Number);
    const yhteensa = vuosi * 12 + (kuukausi - 1) + kuukausia;
    const uusiVuosi = Math.floor(yhteensa / 12);
    const uusiKuukausi = (yhteensa % 12) + 1;
    // Date.UTC(y, m, 0) = kuukauden viimeinen päivä, kun m on nollapohjainen.
    const kuunPituus = new Date(Date.UTC(uusiVuosi, uusiKuukausi, 0)).getUTCDate();
    const uusiPaiva = Math.min(kpv, kuunPituus);
    return `${String(uusiVuosi).padStart(4, '0')}-${String(uusiKuukausi).padStart(2, '0')}-${String(uusiPaiva).padStart(2, '0')}`;
}
//# sourceMappingURL=kehitys.js.map